import type { Item, MixedItemTag, PracticeTestExam } from "@memorang/types";
import { isAttempted } from "./item";
import { compareTagSortOrder, isSkillTag } from "./tag";

export type ProgressBand = "none" | "low" | "medium" | "high";

export const PROGRESS_BAND_THRESHOLDS = {
	low: 40,
	high: 75,
} as const;

export const calculatePointsPercentage = (
	pointsEarned: number,
	pointsTotal: number,
): number => {
	if (pointsTotal <= 0) {
		return 0;
	}
	const earned = Math.max(0, pointsEarned);
	return Math.min(100, Math.round((earned / pointsTotal) * 100));
};

export const getProgressBand = (percent: number): ProgressBand => {
	if (percent <= 0) {
		return "none";
	}
	if (percent < PROGRESS_BAND_THRESHOLDS.low) {
		return "low";
	}
	if (percent < PROGRESS_BAND_THRESHOLDS.high) {
		return "medium";
	}
	return "high";
};

// Tag-level distribution double-counts answered items (backend rollup bug),
// so count answered items directly like the rest of the points math.
export const getAnsweredCount = (tag: MixedItemTag): number =>
	(tag.items ?? []).filter(isAttempted).length;

export const sumItemPoints = (
	items: Item[],
): { numPointsEarned: number; numPointsTotal: number } => {
	let numPointsEarned = 0;
	let numPointsTotal = 0;
	for (const item of items) {
		numPointsEarned += item.stats?.numPointsEarned ?? 0;
		numPointsTotal += item.stats?.numTotalPoints ?? 0;
	}
	return { numPointsEarned, numPointsTotal };
};

export const getTagItemIds = (tag: MixedItemTag): Set<number> =>
	new Set((tag.items ?? []).map((item) => item.id));

export const getAnsweredPoints = (
	tag: MixedItemTag,
): { numPointsEarned: number; numPointsTotal: number } =>
	sumItemPoints((tag.items ?? []).filter(isAttempted));

export type SkillTestScore = {
	/** Which exam produced this score, so callers can tell best from latest. */
	examId: string;
	score: number;
	dateCompleted?: string;
};

export type SkillTestScores = {
	best: SkillTestScore;
	latest: SkillTestScore;
	testsTaken: number;
};

/** Only `id` and `title` are read, so callers holding just those can pass them. */
export type SkillIdentity = Pick<MixedItemTag, "id" | "title">;

const findExamSkillTag = (
	exam: PracticeTestExam,
	skill: SkillIdentity,
): MixedItemTag | undefined =>
	(exam.tagDetails ?? []).find(
		(tag) =>
			isSkillTag(tag) &&
			(tag.id === skill.id ||
				tag.title.toLowerCase() === skill.title.toLowerCase()),
	);

/**
 * Whether an exam covers a skill. Prefers the structural `tagDetails` match —
 * the same signal that decides whether a completed exam produces a score for
 * that skill — and falls back to the title, which is the only signal available
 * when `tagDetails` is unpopulated (common on exams that were never started).
 */
export const examCoversSkill = (
	exam: PracticeTestExam,
	skill: SkillIdentity,
): boolean =>
	Boolean(findExamSkillTag(exam, skill)) ||
	exam.title.toLowerCase().includes(skill.title.toLowerCase());

/**
 * Points an exam contributes to a skill.
 *
 * Prefers the per-skill breakdown in `tagDetails`. Sectional tests arrive with
 * that breakdown empty but carry an exam-level score and a single entry in
 * `topics`, and for a single-skill exam the overall score *is* the skill's
 * score. Multi-skill exams without a breakdown — full mocks, whose `topics` is
 * empty — stay excluded, since their overall score belongs to no one skill.
 */
const getExamSkillPoints = (
	exam: PracticeTestExam,
	skill: SkillIdentity,
): number | undefined => {
	const taggedPoints = findExamSkillTag(exam, skill)?.numPointsEarned;
	if (taggedPoints != null) {
		return taggedPoints;
	}

	const topics = exam.topics ?? [];
	const coversOnlyThisSkill =
		topics.length === 1 &&
		topics[0].toLowerCase() === skill.title.toLowerCase();

	return coversOnlyThisSkill ? (exam.numPointsEarned ?? undefined) : undefined;
};

/**
 * Returns the best and latest scores for a skill from completed practice exams.
 * Only considers summative/practice tests that scored the relevant skill.
 * Returns undefined if no valid results or target score is invalid.
 */
export const calculateSkillTestScores = (
	exams: PracticeTestExam[] | undefined,
	skill: MixedItemTag,
	targetScore: number,
): SkillTestScores | undefined => {
	if (targetScore <= 0) {
		return undefined;
	}

	const scores: SkillTestScore[] = [];

	for (const exam of exams ?? []) {
		// Older exams predate the type field, so fall back to subType for them.
		const isSummative = exam.type
			? exam.type === "SUMMATIVE"
			: exam.subType === "PRACTICE_TEST" || exam.subType === "DIAGNOSTIC";

		if (exam.status !== "COMPLETE" || !isSummative) {
			continue;
		}

		const earned = getExamSkillPoints(exam, skill);
		if (earned == null) {
			continue;
		}

		scores.push({
			examId: exam.id,
			score: Math.min(targetScore, Math.max(0, Math.round(earned))),
			dateCompleted: exam.dateCompleted || undefined,
		});
	}

	if (scores.length === 0) {
		return undefined;
	}

	const best = scores.reduce((top, entry) =>
		entry.score > top.score ? entry : top,
	);

	const latest = scores.reduce((newest, entry) => {
		if (!entry.dateCompleted) {
			return newest;
		}
		if (!newest.dateCompleted) {
			return entry;
		}
		return new Date(entry.dateCompleted) >= new Date(newest.dateCompleted)
			? entry
			: newest;
	});

	return {
		best,
		latest,
		testsTaken: scores.length,
	};
};

export const calculateOverallPointsPercentage = (
	tags: MixedItemTag[],
): number => {
	let earned = 0;
	let total = 0;
	for (const tag of tags) {
		const points = getAnsweredPoints(tag);
		earned += points.numPointsEarned;
		total += points.numPointsTotal;
	}
	return calculatePointsPercentage(earned, total);
};

export type ProficiencyBandPoints = {
	label: string;
	numPointsEarned: number;
	numPointsTotal: number;
	answeredCount: number;
	availableCount: number;
	hasItems: boolean;
	hasAvailableItems: boolean;
};

export type ProficiencyBandFill = {
	label: string;
	percent: number;
	hasItems: boolean;
	hasAvailableItems: boolean;
};

// Fill percents are scaled against proficiencyTargetScore and capped, so a
// band is complete exactly when its fill reaches 100.
export const COMPLETED_FILL_PERCENT = 100;

export const calculateBandFillPercents = (
	bands: ProficiencyBandPoints[],
	targetScore: number,
): ProficiencyBandFill[] =>
	bands.map((band) => {
		const rawPercent =
			band.numPointsTotal > 0
				? (band.numPointsEarned / band.numPointsTotal) * 100
				: 0;
		const percent = calculatePointsPercentage(rawPercent, targetScore);
		// A tiny perfect sample (e.g. 3/3 answered out of 78 available) must not
		// mark a band complete — completion needs the same minimum answered
		// evidence as level estimates, so cap the fill just below complete.
		const requiredAnswered = Math.min(
			PROFICIENCY_LEVEL_MIN_ANSWERED_ITEMS,
			band.availableCount,
		);
		const hasEnoughEvidence = band.answeredCount >= requiredAnswered;
		return {
			label: band.label,
			percent: hasEnoughEvidence
				? percent
				: Math.min(percent, COMPLETED_FILL_PERCENT - 1),
			hasItems: band.hasItems,
			hasAvailableItems: band.hasAvailableItems,
		};
	});

/** Highest band index that has answered items (-1 when none). */
export const getHighestIncludedBandIndex = (
	fills: ProficiencyBandFill[],
): number => {
	for (let i = fills.length - 1; i >= 0; i--) {
		if (fills[i]?.hasItems) {
			return i;
		}
	}
	return -1;
};

/** Highest band index at completed fill (-1 when none). */
export const getHighestCompletedBandIndex = (
	fills: ProficiencyBandFill[],
): number => {
	let highest = -1;
	for (let i = 0; i < fills.length; i++) {
		if ((fills[i]?.percent ?? 0) >= COMPLETED_FILL_PERCENT) {
			highest = i;
		}
	}
	return highest;
};

/**
 * A band is complete when its fill reaches 100, when it sits below the highest
 * completed band (fill-previous), or when it is an empty lower band treated as
 * tested-out because a higher band has items.
 */
export const isProficiencyBandComplete = (
	fill: ProficiencyBandFill,
	index: number,
	highestIncludedIndex: number,
	highestCompletedIndex: number,
): boolean => {
	if (fill.percent >= COMPLETED_FILL_PERCENT) {
		return true;
	}
	if (highestCompletedIndex >= 0 && index < highestCompletedIndex) {
		return true;
	}
	return (
		!fill.hasItems && highestIncludedIndex >= 0 && index < highestIncludedIndex
	);
};

/**
 * First band that is not complete for progression / tips / estimates.
 * Empty lower bands count as tested-out when a higher band has items.
 * Returns -1 when fills is empty or every band is complete.
 */
export const getCurrentProficiencyBandIndex = (
	fills: ProficiencyBandFill[],
): number => {
	if (fills.length === 0) {
		return -1;
	}
	const highestIncludedIndex = getHighestIncludedBandIndex(fills);
	const highestCompletedIndex = getHighestCompletedBandIndex(fills);
	return fills.findIndex(
		(fill, i) =>
			!isProficiencyBandComplete(
				fill,
				i,
				highestIncludedIndex,
				highestCompletedIndex,
			),
	);
};

export const buildProficiencyBandFills = (
	proficiencyTags: MixedItemTag[],
	targetScore: number,
	itemIds?: Set<number>,
): ProficiencyBandFill[] => {
	const ordered = [...proficiencyTags].sort(compareTagSortOrder);
	const bands = ordered.map((tag) => {
		const items = tag.items ?? [];
		const scopedItems = itemIds
			? items.filter((item) => itemIds.has(item.id))
			: items;
		const answeredItems = scopedItems.filter(isAttempted);
		const { numPointsEarned, numPointsTotal } = sumItemPoints(answeredItems);
		return {
			label: tag.title,
			numPointsEarned,
			numPointsTotal,
			answeredCount: answeredItems.length,
			availableCount: scopedItems.length,
			hasItems: answeredItems.length > 0,
			hasAvailableItems: scopedItems.length > 0,
		};
	});
	return calculateBandFillPercents(bands, targetScore);
};

/**
 * Minimum answered items a band needs before `calculateBandFillPercents` will
 * let its fill read as complete (100%). Shrinks to the band's available item
 * count when fewer items exist in scope, so a tiny perfect sample can't mark
 * a band complete on thin evidence.
 */
export const PROFICIENCY_LEVEL_MIN_ANSWERED_ITEMS = 5;

export type SessionProficiencyEstimate = {
	currentLevel: string;
	nextLevel?: string;
	progress: number;
	readyToAdvance: boolean;
};

export const getSessionProficiencyEstimate = (
	fills: ProficiencyBandFill[],
): SessionProficiencyEstimate | undefined => {
	if (fills.length === 0) {
		return undefined;
	}
	const foundIndex = getCurrentProficiencyBandIndex(fills);
	const currentIndex = foundIndex >= 0 ? foundIndex : fills.length - 1;
	const currentFill = fills[currentIndex];
	if (!currentFill) {
		return undefined;
	}
	const nextFill = fills[currentIndex + 1];
	const progress = currentFill.percent;
	const nextLevel = nextFill?.label;
	return {
		currentLevel: currentFill.label,
		...(nextLevel ? { nextLevel } : {}),
		progress,
		readyToAdvance: progress >= COMPLETED_FILL_PERCENT && Boolean(nextLevel),
	};
};

export type SkillProficiencyEstimate = SessionProficiencyEstimate & {
	skillId: MixedItemTag["id"];
	skillTitle: string;
};

export const buildSkillProficiencyEstimates = ({
	skillTags,
	proficiencyTags,
	proficiencyTargetScore,
}: {
	skillTags: MixedItemTag[];
	proficiencyTags: MixedItemTag[];
	proficiencyTargetScore: number;
}): SkillProficiencyEstimate[] =>
	skillTags.flatMap((skill) => {
		const fills = buildProficiencyBandFills(
			proficiencyTags,
			proficiencyTargetScore,
			getTagItemIds(skill),
		);
		const estimate = getSessionProficiencyEstimate(fills);
		if (!estimate) {
			return [];
		}
		return [
			{
				skillId: skill.id,
				skillTitle: skill.title,
				...estimate,
			},
		];
	});

export const groupQuestionTypesBySkill = (
	skillTags: MixedItemTag[],
	questionTypeTags: MixedItemTag[],
): Map<number, MixedItemTag[]> => {
	const skillItemIds = new Map(
		skillTags.map((skill) => [skill.id, getTagItemIds(skill)]),
	);

	const grouped = new Map<number, MixedItemTag[]>();
	for (const questionType of [...questionTypeTags].sort(compareTagSortOrder)) {
		const skill = skillTags.find((candidate) =>
			(questionType.items ?? []).some((item) =>
				skillItemIds.get(candidate.id)?.has(item.id),
			),
		);
		if (!skill) {
			continue;
		}
		const rows = grouped.get(skill.id) ?? [];
		rows.push(questionType);
		grouped.set(skill.id, rows);
	}
	return grouped;
};

/**
 * Get the lowest configured proficiency level's title.
 * Undefined only when the app has no proficiency tags at all.
 */
export const getFallbackProficiencyLevelLabel = (
	proficiencyTags: MixedItemTag[],
): string | undefined =>
	[...proficiencyTags].sort(compareTagSortOrder)[0]?.title;
